import { TFunction } from "i18next";

import { buildCourseRouteSummaryLabel } from "@ryde/controls";
import { ICoursesCalendarSlotDto, RideStatusEnum } from "@ryde/shared";

import {
  CALENDAR_HOUR_LABEL_PAD_LENGTH,
  COURSES_CALENDAR_CELL_VISIBLE_SLOT_COUNT,
} from "../courses-calendar.constants";

export interface ICoursesCalendarPassengerCounts {
  onRide: number;
  notOnRide: number;
}

export const getCoursesCalendarPassengerCounts = (
  slot: ICoursesCalendarSlotDto,
): ICoursesCalendarPassengerCounts => {
  const onRide = slot.hasRide ? (slot.ridePassengerCount ?? 0) : 0;
  const notOnRide = slot.hasRide
    ? Math.max(0, slot.potentialPassengerCount - onRide)
    : slot.potentialPassengerCount;

  return { onRide, notOnRide };
};

export const getCoursesCalendarPassengersModifierClass = (
  slot: ICoursesCalendarSlotDto,
  counts: ICoursesCalendarPassengerCounts,
): string => {
  if (counts.onRide === 0 && counts.notOnRide === 0) {
    return "cc-passengers--empty";
  }
  if (slot.hasRide) {
    return "cc-passengers--ride";
  }
  return "cc-passengers--potential";
};

export const coursesCalendarSlotKey = (dateKey: string, hour: number): string =>
  `${dateKey}|${hour}`;

export const formatCoursesCalendarHourLabel = (hourIndex: number): string => {
  if (hourIndex === 24) {
    return "24:00";
  }

  return `${String(hourIndex).padStart(CALENDAR_HOUR_LABEL_PAD_LENGTH, "0")}:00`;
};

export const getCoursesCalendarCellOverflowCount = (
  totalCount: number,
  visibleCount = COURSES_CALENDAR_CELL_VISIBLE_SLOT_COUNT,
): number => Math.max(0, totalCount - visibleCount);

export const getCoursesCalendarCourseRouteSummaryLabel = (
  t: TFunction,
  slot: ICoursesCalendarSlotDto,
): string | null =>
  buildCourseRouteSummaryLabel({
    t,
    courseTotalDuration: slot.courseTotalDuration ?? 0,
    courseStationsCount: slot.courseStationsCount ?? 0,
  });

export const coursesCalendarHasPendingBookings = (
  slot: ICoursesCalendarSlotDto,
): boolean => {
  if (slot.hasRide) {
    return false;
  }
  return getCoursesCalendarPassengerCounts(slot).notOnRide > 0;
};

export const buildCoursesCalendarCellSelectAllLabel = ({
  t,
  isAllSelected,
  totalCount,
}: {
  t: TFunction;
  isAllSelected: boolean;
  totalCount: number;
}): string => {
  if (totalCount <= 1) {
    return t(isAllSelected ? "booking.deselect" : "booking.select");
  }

  return t(
    isAllSelected
      ? "course.calendar.cellDeselectAllWithCount"
      : "course.calendar.cellSelectAllWithCount",
    { count: totalCount },
  );
};

export const buildCoursesCalendarCellSelectionBadges = ({
  t,
}: {
  t: TFunction;
}): { selectedPrefix: string; fromPrefix: string } => ({
  selectedPrefix: t("course.calendar.cellSelectedPrefix"),
  fromPrefix: t("course.calendar.cellFromPrefix"),
});

export const coursesCalendarRideCardModifierClass = (
  slot: ICoursesCalendarSlotDto,
): string => {
  if (!slot.hasRide || typeof slot.rideId !== "number") {
    if (coursesCalendarHasPendingBookings(slot)) {
      return "cc-item--not-created-bookings";
    }
    return "cc-item--not-created";
  }
  if (slot.rideStatus === RideStatusEnum.Canceled) {
    return "cc-item--cancelled";
  }
  return "cc-item--active";
};
